import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { track } from "@/analytics";
import { fetchMe } from "@/api/users";
import { useAppFeatures } from "@/store/appFeatures";
import { useAuth, type PaymentMethodMe } from "@/store/authStore";
import {
  CARD_CHANGED_REDIRECT,
  YOOKASSA_REDIRECT,
} from "../constants";
import { hasRedirectParam } from "../utils";
import { requireContext, type WithChildren } from "./shared";

type MainSubscriptionContextValue = {
  paymentMethods: PaymentMethodMe[];
  selectedPaymentMethod: PaymentMethodMe | null;
  isPaid: boolean;
  isCancelled: boolean;
  /** Подписка по промокоду: автопродления нет, «Отменить»/«Возобновить» не показываем. */
  isPromo: boolean;
  subscriptionEndDate: string;
  isConfigReady: boolean;
  configLink: string | null;
  successOpen: boolean;
  closeSuccess: () => void;
};

const MainSubscriptionContext =
  createContext<MainSubscriptionContextValue | null>(null);

export function useMainSubscriptionContext() {
  return requireContext(
    useContext(MainSubscriptionContext),
    "useMainSubscriptionContext",
  );
}

export function MainSubscriptionProvider({ children }: WithChildren) {
  const me = useAuth((s) => s.me);
  const setMe = useAuth((s) => s.setMe);
  const accessToken = useAuth((s) => s.accessToken);
  const loadFeatures = useAppFeatures((s) => s.load);
  const promoSubscription = useAppFeatures((s) => s.promo_subscription);
  const [searchParams] = useSearchParams();
  const [successOpen, setSuccessOpen] = useState(false);

  // выключатели бэка и «подписка по промокоду» — заново при входе и при смене подписки
  useEffect(() => {
    loadFeatures();
  }, [
    loadFeatures,
    accessToken,
    me?.subscription?.id,
    me?.subscription?.expires_at,
    me?.subscription?.auto_renew,
  ]);

  const isYookassaRedirect = useMemo(() => {
    return hasRedirectParam(searchParams, YOOKASSA_REDIRECT);
  }, [searchParams]);

  const isCardChangedRedirect = useMemo(() => {
    return hasRedirectParam(searchParams, CARD_CHANGED_REDIRECT);
  }, [searchParams]);

  useEffect(() => {
    if (!accessToken) return;
    if (!isYookassaRedirect && !isCardChangedRedirect) return;

    let cancelled = false;

    const refresh = async () => {
      try {
        const data = await fetchMe();
        if (cancelled) return;

        setMe(data);

        const hasPaidSubscription =
          new Date(data.subscription?.expires_at ?? 0) > new Date();
        const hasConfig = Boolean(data.subscription?.config_url);

        if (isYookassaRedirect && hasPaidSubscription && hasConfig) {
          setSuccessOpen(true);
          track("bkn.success_modal.show");
        }
      } catch {
        // Leave current state as-is if refresh fails after redirect.
      }
    };

    refresh();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isCardChangedRedirect, isYookassaRedirect, setMe]);

  const subscriptionEndDate = useMemo(() => {
    const expires = me?.subscription?.expires_at;
    if (!expires) return "—";
    return new Date(expires).toLocaleDateString("ru-RU");
  }, [me?.subscription?.expires_at]);

  const isPaid = new Date(me?.subscription?.expires_at ?? 0) > new Date();
  const isPromo =
    isPaid && promoSubscription === true && !me?.subscription?.auto_renew;
  const isCancelled = isPaid && !me?.subscription?.auto_renew && !isPromo;
  const configLink = me?.subscription?.config_url ?? null;
  const isConfigReady = Boolean(configLink);
  const paymentMethods = me?.payment_methods ?? [];
  const selectedPaymentMethod =
    paymentMethods.find((method) => method.is_selected) ?? null;

  const value = useMemo<MainSubscriptionContextValue>(
    () => ({
      paymentMethods,
      selectedPaymentMethod,
      isPaid,
      isCancelled,
      isPromo,
      subscriptionEndDate,
      isConfigReady,
      configLink,
      successOpen,
      closeSuccess: () => setSuccessOpen(false),
    }),
    [
      configLink,
      isCancelled,
      isConfigReady,
      isPaid,
      isPromo,
      paymentMethods,
      selectedPaymentMethod,
      subscriptionEndDate,
      successOpen,
    ],
  );

  return (
    <MainSubscriptionContext.Provider value={value}>
      {children}
    </MainSubscriptionContext.Provider>
  );
}
